import arrayDiff from 'src/utils/arrayDiff'
import { Promise } from 'bluebird'

const state = {
  id: null,
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  img: '',
  canCloseIssues: false,
  recipients: [],
  agencies: [],
  residences: [],
  places: [],
  tmpPlaces: [],
  notifications: [],
  notificationsSeen: [],
  token: '',
  scopes: [],
  isLoadingPlace: false,
  isSyncingTmpItems: false,
  isSyncingUserPlaces: false,
  isLoadingIssues: false,
  loadingIssuesPercent: 0,
  issuesToReplicateCount: 0,
  placesToLoadCount: 0,
  qrCode: null
}

function findResidenceInState(state, agencyId, residenceId) {
  let agencyInState = state.agencies.find((el) => el.id === agencyId)
  let residenceInSate = agencyInState.residences.find(
    (el) => el.id === residenceId
  )
  return residenceInSate
}

const getters = {
  attributes: (state) => {
    // *
    return {
      id: state.id,
      firstName: state.firstName,
      lastName: state.lastName,
      img: state.img,
      canCloseIssues: Boolean(state.canCloseIssues),
      recipients: state.recipients,
      scopes: state.scopes
    }
  },
  token: (state) => state.token,
  agencies: (state) => state.agencies,
  residences: (state) => state.residences,
  places: (state) => state.places,
  notifications: (state) => state.notifications,
  notificationsSeen: (state) => state.notificationsSeen,
  isLoadingPlace: (state) => state.isLoadingPlace,
  isSyncingTmpItems: (state) => state.isSyncingTmpItems,
  isSyncingUserPlaces: (state) => state.isSyncingUserPlaces,
  isLoadingIssues: (state) => state.isLoadingIssues,
  loadingIssuesPercent: (state) => state.loadingIssuesPercent,
  placesToLoadCount: (state) => state.placesToLoadCount,
  qrCode: (state) => state.qrCode
}

const actions = {
  authenticate({ commit, dispatch }, { credentials, device, appVersion }) {
    return new Promise((resolve) => {
      this._vm.$SowellProxy
        .authenticate(credentials, device, appVersion)
        .then((response) => {
          commit('SET_TOKEN', response.data.jwt)
          dispatch('setReporter').then((reporter) => {
            resolve(reporter)
          })
        })
    })
  },
  msalAuthenticate({ commit, dispatch }, { credentials, device, appVersion }) {
    return new Promise((resolve) => {
      this._vm.$SowellProxy
        .msalAuthenticate(credentials, device, appVersion)
        .then((response) => {
          commit('SET_TOKEN', response.data.jwt)
          dispatch('setReporter').then((reporter) => {
            resolve(reporter)
          })
        })
    })
  },
  setToken: ({ commit }, token) => {
    commit('SET_TOKEN', token)
  },
  setReporter({ commit, dispatch }) {
    return new Promise((resolve) => {
      commit('SET_IS_LOADING_PLACES', false)
      this._vm.$SowellProxy.loadReporter().then((response) => {
        let reporter = {
          id: Math.trunc(response.data.id),
          email: response.data.email,
          firstName: response.data['first-name'],
          lastName: response.data['last-name'],
          recipients: response.data.recipients,
          img: response.data.img,
          canCloseIssues: response.data['can-close-issues'],
          company: response.data.company
        }

        this._vm.$SowellProxy.loadReporterScopes().then((scopes) => {
          reporter.scopes = scopes.data.map((key) => key.id)
          commit('SET_ALL', reporter)
          dispatch('preparePatrimonySync', {
            company: response.data.company,
            lastChangeDate: response.data.company['updated-at']
          }).then(() => {
            resolve(reporter)
          })
        })
      })
    })
  },
  preparePatrimonySync: (
    { rootGetters, dispatch },
    { company, lastChangeDate }
  ) => {
    return new Promise(async (resolve) => {
      const remoteDataUpdatedAt = new Date(lastChangeDate).getTime()
      const localDataUpdatedAt = new Date(
        rootGetters['company/attributes'].updatedAt
      ).getTime()
      if (remoteDataUpdatedAt >= localDataUpdatedAt) {
        /*
          NOTE: Never logged in before or one of the company assets (zones/places/spots)
          has changed since the last download so that we retrieve all
        */
        await dispatch('company/load', company, {
          root: true
        })
        await dispatch('preloadPatrimoine')
        resolve()
      } else {
        resolve()
      }
    })
  },
  async preloadPatrimoine() {
    return new Promise(async (resolve) => {
      await this._vm.$DB.resetLocalPlaces()
      const reporterPlacesIds =
        await this._vm.$SowellProxy.loadReporterPlacesIds()

      const localPlacesIds = (
        await this._vm.$patrimoine.placesCollection().find({ selector: {} })
      ).docs.map((doc) => doc._id)
      const remotePlacesIds = reporterPlacesIds.map((p) => p.id)

      const placesDiff = arrayDiff(remotePlacesIds, localPlacesIds)
      const placesToUnload = placesDiff.missingLeft
      await Promise.each(placesToUnload, async (placeId) => {
        const place = await this._vm.$patrimoine.placesCollection().get(placeId)
        if (place) {
          await this._vm.$patrimoine.placesCollection().remove(place)
        }
      })

      const placesToLoad = reporterPlacesIds.filter((p) => {
        return placesDiff.missingRight.includes(p.id)
      })
      await this._vm.$DB.fillTmpPlaces(placesToLoad)

      return resolve()
    })
  },
  sendAlert({ commit }, next) {
    // *
    return new Promise((resolve) => {
      this._vm.$SowellProxy
        .sendAlert(next.lonlat, next.message)
        .then((response) => resolve(response))
    })
  },
  logout({ commit }) {
    // *
    commit('LOGOUT')
  },
  setNotifications({ commit }, notifications) {
    commit('SET_NOTIFICATIONS', notifications)
  },
  setNotificationsSeen({ commit }, notifications) {
    commit('SET_NOTIFICATIONS_SEEN', notifications)
  },
  clearIssueNotifications({ commit }, issue) {
    commit('CLEAR_ISSUE_NOTIFICATIONS', issue)
  },
  reset({ dispatch, commit }) {
    // *
    commit('RESET')
    dispatch('audits/reset', null, { root: true })
    dispatch('company/reset', null, { root: true })
    dispatch('common/reset', null, { root: true })
  },
  async loadAgencies({ commit }) {
    const result = await this._vm.$patrimoine.agenciesCollection().find({
      selector: {},
      fields: ['_id', 'name']
    })
    commit('SET_AGENCIES', result.docs)
  },
  async loadResidences({ commit }, agencyId) {
    const result = await this._vm.$patrimoine.residencesCollection().find({
      selector: { agency: { $regex: String(agencyId) } },
      fields: ['_id', 'name', 'agency']
    })
    commit('SET_RESIDENCES', result.docs)
  },
  async loadPlaces({ commit }, residenceId) {
    const result = await this._vm.$patrimoine.placesCollection().find({
      selector: { residence: { $regex: String(residenceId) } },
      fields: ['_id', 'name', 'residence', 'spots']
    })
    commit('SET_PLACES', result.docs)
  },
  async fetchProviders({ rootGetters, state }) {
    return new Promise(async (resolve) => {
      const companyId = rootGetters['company/attributes'].id
      const response = await this._vm.$SowellProxy.loadCompanyProviders(companyId)
      await this._vm.$DB.fillLocalProviders(response.map((provider) => {
        return {
          email: provider.email,
          firstName: provider['first-name'],
          lastName: provider['last-name']
        }
      }))
      resolve(response)
    })
  },
  async loadProviders({ state }) {
    const result = await this._vm.$DB.localProviders().find({
      selector: {},
      fields: ['email', 'firstName', 'lastName']
    })
    return result.docs
  },
  async sendFailedRequests({ state }, message) {
    const { reports, issues } = await this._vm.$DB.pendingTmpItems()
    const { Device } = await import('Capacitor/@capacitor/device')
    const info = await Device.getInfo()
    const device = {
      manufacturer: info.manufacturer,
      model: info.model,
      platform: info.platform,
      version: info.osVersion
    }
    const meta = {
      email: state.email,
      app: 'Reporter V1',
      version: process.env.APP_VERSION,
      device
    }
    return await this._vm.$SowellProxy.sendFailedRequests({
      json: { reports, issues, meta },
      message
    })
  },
  setPlacesToLoadCount({ commit }, count) {
    commit('SET_PLACES_TO_LOAD_COUNT', count)
  },
  setQrCode({ commit }, value) {
    commit('SET_QR_CODE', value)
  },
  async fetchAllUsers({ rootGetters, state }) {
    return new Promise(async (resolve) => {
      const companyId = rootGetters['company/attributes'].id
      const response = await this._vm.$SowellProxy.loadAllUsers(companyId)
      const filteredUsers = response
        .filter((user) => {
          const status = user.attributes?.status || user.status
          return status !== 'suspended' && !user.attributes?.email.startsWith("sw-")
        })
        .map((user) => {
          const userData = user.attributes || user
          return {
            email: userData.email,
            firstName: userData['first-name'],
            lastName: userData['last-name'],
            status: userData.status
          }
        })
      await this._vm.$DB.fillLocalUsers(filteredUsers)
      resolve(filteredUsers)
    })
  },
  async loadAllUsers({ state }) {
    const result = await this._vm.$DB.localUsers().find({
      selector: {
        status: { $ne: 'suspended' }
      },
      fields: ['email', 'firstName', 'lastName', 'status']
    })
    return result.docs
  },
}

const mutations = {
  SET_ALL(state, reporter) {
    state.id = reporter.id
    state.email = reporter.email
    state.firstName = reporter.firstName
    state.lastName = reporter.lastName
    state.img = reporter.img
    state.canCloseIssues = reporter.canCloseIssues
    state.recipients = reporter.recipients
    state.scopes = reporter.scopes
  },
  SET_TOKEN(state, next) {
    state.token = next
  },
  SET_NOTIFICATIONS(state, notifications) {
    state.notifications = notifications
  },
  SET_NOTIFICATIONS_SEEN(state, notifications) {
    state.notificationsSeen = notifications
  },
  CLEAR_ISSUE_NOTIFICATIONS(state, issue) {
    const notification = state.notifications.find((notification) => {
      return notification.id === issue.meta.id
    })
    state.notifications = state.notifications.filter(
      (notification) => notification.id !== issue.meta.id
    )
    if (notification) {
      state.notificationsSeen.push(notification)
    }
  },
  CLEAR_NOTIFICATIONS_SEEN(state) {
    state.notificationsSeen = []
  },
  LOGOUT(state) {
    state.token = null
  },
  RESET(state) {
    // NOTE: We don't reset the reporter email/password so that we can auto populate corresponding fields
    state.id = null
    state.firstName = ''
    state.lastName = ''
    state.img = ''
    state.canCloseIssues = false
    state.recipients = []
    state.agencies = []
    state.token = null
    state.notifications = []
    state.notificationsSeen = []
    state.scopes = []
  },
  SET_IS_LOADING_PLACES(state, value) {
    state.isLoadingPlace = value
  },
  SET_IS_SYNCING_TMP_ITEMS(state, value) {
    //TODO: commit SET_HAS_PENDING_REPORTS
    state.isSyncingTmpItems = value
  },
  SET_IS_SYNCING_USER_PLACES(state, value) {
    state.isSyncingUserPlaces = value
  },
  SET_IS_LOADING_ISSUES(state, isLoadingIssues) {
    state.isLoadingIssues = isLoadingIssues
  },
  SET_LOADING_ISSUES_PERCENT(state, progress) {
    if (!progress) {
      progress = 0
    } else if (progress < 0) {
      progress = 0
    }

    if (progress > state.issuesToReplicateCount) {
      state.issuesToReplicateCount = progress
    }

    const replicatedCount = state.issuesToReplicateCount - progress

    state.loadingIssuesPercent = Math.floor(
      (replicatedCount / state.issuesToReplicateCount) * 100
    )
  },
  SET_ISSUES_TO_REPLICATE_COUNT(state, count) {
    state.issuesToReplicateCount = count
  },
  SET_AGENCIES(state, agencies) {
    state.agencies = agencies.map((agency) => {
      if (agency._id && !agency.id) {
        agency.id = agency._id
      }
      return agency
    })
  },
  SET_RESIDENCES(state, residences) {
    state.residences = residences.map((residence) => {
      if (residence._id && !residence.id) {
        residence.id = residence._id
      }
      return residence
    })
  },
  SET_PLACES(state, places) {
    state.places = places.map((place) => {
      if (place._id && !place.id) {
        place.id = place._id
      }
      return place
    })
  },
  SET_PLACES_TO_LOAD_COUNT(state, count) {
    state.placesToLoadCount = count
  },
  SET_QR_CODE(state, value) {
    state.qrCode = value
  }
}
export default {
  state,
  getters,
  actions,
  mutations,
  namespaced: true
}
